import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  Label,
  LoadingSkeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  formatDate,
  formatNumber,
} from '@berky/ui'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, CalendarDays, Search } from 'lucide-react'
import { useState } from 'react'

import {
  usePlatformUsageApi,
  type UsageActivityRow,
  type UsageAdoptionPerson,
  type UsagePageResult,
  type UsageSummary,
} from './api'
import {
  berlinDateTime,
  comparison,
  moduleTitle,
  queryFilters,
  type UsageFilters,
} from './usage-utils'

export function UsageAdoption({ filters }: { filters: UsageFilters }) {
  const api = usePlatformUsageApi()
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState('')
  const [sort, setSort] = useState('recent')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<UsageAdoptionPerson | null>(null)
  const summary = useQuery({
    queryKey: ['platform-usage', 'summary', filters],
    queryFn: ({ signal }) =>
      api.get<UsageSummary>('/summary', { query: queryFilters(filters), signal }),
  })
  const people = useQuery({
    queryKey: ['platform-usage', 'adoption-people', filters, search, sort, page],
    queryFn: ({ signal }) =>
      api.get<UsagePageResult<UsageAdoptionPerson>>('/adoption/people', {
        query: {
          ...queryFilters(filters),
          suche: search || undefined,
          sortierung: sort,
          seite: page,
        },
        signal,
      }),
  })
  const report = summary.data

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Wer nutzt Berky Digital?</CardTitle>
          <CardDescription>
            Der letzte Zugriff gilt unabhängig vom gewählten Zeitraum. Alle übrigen Zahlen
            beziehen sich auf den Filter oben. Wählen Sie eine Person für ihren
            Modulverlauf.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <form
              className="flex min-w-64 flex-1 items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault()
                setSearch(draft.trim())
                setPage(1)
              }}
            >
              <div className="grid flex-1 gap-1.5">
                <Label htmlFor="adoption-person-search">Person suchen</Label>
                <Input
                  id="adoption-person-search"
                  value={draft}
                  maxLength={320}
                  placeholder="Name oder E-Mail-Adresse"
                  onChange={(event) => setDraft(event.target.value)}
                />
              </div>
              <Button type="submit" variant="outline">
                <Search aria-hidden="true" />
                Suchen
              </Button>
            </form>
            <div className="grid min-w-56 gap-1.5">
              <Label htmlFor="adoption-sort">Sortieren</Label>
              <Select
                value={sort}
                onValueChange={(value) => {
                  setSort(value)
                  setPage(1)
                }}
              >
                <SelectTrigger id="adoption-sort" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Zuletzt aktiv</SelectItem>
                  <SelectItem value="inactive">Längste Inaktivität</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="active-days">Meiste aktive Tage</SelectItem>
                  <SelectItem value="actions">Meiste Aktionen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DataTable
            rows={people.data?.items ?? []}
            rowKey={(person) => person.userId}
            caption="Nutzeraktivität in Berky Digital"
            isLoading={people.isPending}
            pagination={people.data}
            onPageChange={setPage}
            error={
              people.isError
                ? {
                    description: 'Die Nutzeraktivität konnte nicht geladen werden.',
                    onRetry: () => void people.refetch(),
                  }
                : null
            }
            emptyState={
              <EmptyState
                title="Keine Personen gefunden"
                description="Bitte passen Sie die Suche oder den gewählten Bereich an."
              />
            }
            columns={[
              {
                key: 'person',
                header: 'Person',
                className: 'sticky left-0 z-10 min-w-48 bg-card',
                cell: (person) => (
                  <div>
                    <Button
                      variant="link"
                      className="h-auto whitespace-normal p-0 text-left"
                      onClick={() => setSelected(person)}
                    >
                      {person.userName}
                      <ArrowRight aria-hidden="true" />
                    </Button>
                    <p className="text-xs text-muted-foreground">{person.userEmail}</p>
                  </div>
                ),
              },
              {
                key: 'lastAccess',
                header: 'Letzter Zugriff',
                cell: (person) =>
                  person.lastAccessAt ? (
                    <div>
                      <p>{berlinDateTime(person.lastAccessAt)}</p>
                      <p className="text-xs text-muted-foreground">
                        {person.lastModuleId
                          ? moduleTitle(person.lastModuleId)
                          : 'Bereich unbekannt'}
                      </p>
                    </div>
                  ) : (
                    <Badge variant="secondary">Noch nicht genutzt</Badge>
                  ),
              },
              {
                key: 'activeDays',
                header: 'Aktive Tage',
                align: 'right',
                cell: (person) => formatNumber(person.activeDays),
              },
              {
                key: 'modules',
                header: 'Module · Einstiege',
                cell: (person) =>
                  person.modules.length ? (
                    <ul className="min-w-56 space-y-1.5">
                      {person.modules.map((module) => (
                        <li
                          key={module.moduleId}
                          className="flex items-center justify-between gap-4"
                        >
                          <span>{moduleTitle(module.moduleId)}</span>
                          <Badge variant="secondary">
                            {formatNumber(module.entries)}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-muted-foreground">–</span>
                  ),
              },
              {
                key: 'entries',
                header: 'Einstiege gesamt',
                align: 'right',
                cell: (person) => formatNumber(person.entries),
              },
              {
                key: 'actions',
                header: 'Aktionen',
                align: 'right',
                cell: (person) => formatNumber(person.actions),
              },
              {
                key: 'records',
                header: 'Datensatzänderungen',
                align: 'right',
                cell: (person) => formatNumber(person.changedRecords),
              },
            ]}
          />
        </CardContent>
      </Card>
      {summary.isPending ? (
        <LoadingSkeleton variant="text" rows={4} />
      ) : summary.isError ? (
        <ErrorState
          description="Die Kennzahlen zur Nutzeraktivität konnten nicht geladen werden."
          onRetry={() => void summary.refetch()}
        />
      ) : (
        report && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Metric
                label="Aktive Nutzer"
                value={formatNumber(report.activeUsers)}
                detail={comparison(report.activeUsers, report.previousActiveUsers)}
              />
              <Metric
                label="Ohne Nutzung im Zeitraum"
                value={formatNumber(
                  Math.max(0, report.knownUsers - report.activeKnownUsers),
                )}
                detail={`${formatNumber(report.activeKnownUsers)} von ${formatNumber(report.knownUsers)} bekannten, nicht gesperrten Nutzern aktiv`}
              />
              <Metric
                label="Erfolgreiche Aktionen"
                value={formatNumber(report.actions)}
                detail={activityComparison(report.actions, report.previousActions)}
              />
              <Metric
                label="Datensatzänderungen"
                value={formatNumber(report.changedRecords)}
                detail={activityComparison(
                  report.changedRecords,
                  report.previousChangedRecords,
                )}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Aktionen und Datensatzänderungen erscheinen, sobald ein Modul erfolgreiche
              Fachvorgänge an die Plattform meldet. Mehrfachänderungen desselben
              Datensatzes zählen mehrfach; Inhalte und Datensatzkennungen werden nicht
              gespeichert.
            </p>
          </>
        )
      )}
      {selected && (
        <AdoptionTimeline
          person={selected}
          filters={filters}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

function Metric({
  label,
  value,
  detail,
}: {
  label: string
  value: string
  detail: string
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-3xl font-semibold tracking-tight" data-numeric>
          {value}
        </p>
        <p className="text-sm text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  )
}

function activityComparison(current: number, previous: number) {
  if (current === 0 && previous === 0) return 'Noch keine fachliche Aktivität gemeldet'
  return comparison(current, previous)
}

function AdoptionTimeline({
  person,
  filters,
  onClose,
}: {
  person: UsageAdoptionPerson
  filters: UsageFilters
  onClose: () => void
}) {
  const api = usePlatformUsageApi()
  const [page, setPage] = useState(1)
  const activity = useQuery({
    queryKey: ['platform-usage', 'adoption-person', person.userId, filters, page],
    queryFn: ({ signal }) =>
      api.get<UsagePageResult<UsageActivityRow>>(`/adoption/people/${person.userId}`, {
        query: { ...queryFilters(filters), seite: page },
        signal,
      }),
  })
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Modulaktivität · {person.userName}</SheetTitle>
          <SheetDescription>
            {formatDate(filters.from)} – {formatDate(filters.to)} · Neueste Nutzung zuerst
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-5 px-4 pb-6">
          {activity.isPending ? (
            <LoadingSkeleton variant="text" rows={8} />
          ) : activity.isError ? (
            <ErrorState
              description="Der Modulverlauf konnte nicht geladen werden."
              onRetry={() => void activity.refetch()}
            />
          ) : !activity.data?.items.length ? (
            <EmptyState
              title="Keine Nutzung im gewählten Zeitraum"
              description="Wählen Sie einen längeren Zeitraum oder einen anderen Bereich."
            />
          ) : (
            <>
              <ol className="space-y-3" aria-label="Modulaktivität">
                {activity.data.items.map((row) => (
                  <li
                    key={`${row.usageDate}:${row.moduleId}`}
                    className="rounded-lg border p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">{moduleTitle(row.moduleId)}</p>
                        <p className="text-sm text-muted-foreground">
                          Letzter Zugriff {berlinDateTime(row.lastAccessAt)}
                        </p>
                      </div>
                      <Badge variant="secondary">
                        <CalendarDays aria-hidden="true" />
                        {formatDate(row.usageDate)}
                      </Badge>
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground">
                      {formatNumber(row.entries)} Einstiege · {formatNumber(row.actions)}
                      {' Aktionen · '}
                      {formatNumber(row.changedRecords)} Datensatzänderungen
                    </p>
                  </li>
                ))}
              </ol>
              <div className="flex items-center justify-between gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Neuer
                </Button>
                <p className="text-xs text-muted-foreground">
                  Seite {page} von{' '}
                  {Math.max(1, Math.ceil(activity.data.total / activity.data.pageSize))}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page * activity.data.pageSize >= activity.data.total}
                  onClick={() => setPage(page + 1)}
                >
                  Älter
                </Button>
              </div>
            </>
          )}
          <p className="text-xs text-muted-foreground">
            Es werden nur Zähler gespeichert. Datensatzkennungen und Inhalte sind nicht
            Bestandteil dieser Auswertung.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  )
}
